import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PurchaseSuccess from "./PurchaseSuccess";
import ApiServices from "../../../services/ApiServices";
import { GET_AD } from "../../../constants/api-constants";
import { useLoading } from "../../../hooks/useLoader";
import { INT_TO_POSITION } from "../components/Utils/adUtils";

export const status = {
  ACTIVE: "Active",
  INACTIVE: "Inactive",
  DRAFT: "Draft",
};

const DASH = "-";

const formatDateRange = (start, end) => {
  if (!start && !end) return DASH;
  const fmt = (d) => {
    if (!d) return "";
    const parsed = new Date(d);
    if (Number.isNaN(parsed.getTime())) return d;
    return parsed.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };
  return `${fmt(start)} – ${fmt(end)}`;
};

const valueOrDash = (v) => {
  if (v === null || v === undefined || v === "") return DASH;
  return v;
};

const mapAdToDetails = (record) => {
  if (!record) return null;
  const bannerName = record.title ?? record.bannerName;
  const position = INT_TO_POSITION[record.bannerPosition] ?? record.position;
  const amount =
    record.amount ?? record.estimatedCost ?? record.cost ?? record.totalAmount;
  const isPurchased =
    record.paymentStatus === 1 || record.paymentStatusText === "Purchased";
  const statusValue = isPurchased
    ? status.ACTIVE
    : (record.statusText ?? record.paymentStatusText ?? record.status);
  return {
    bannerName,
    position,
    startDate: record.startDate,
    endDate: record.endDate,
    amount,
    status: statusValue,
    distributorId: record.distributorId,
    isPurchased,
  };
};

const AdPaymentSuccess = () => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const { showLoader } = useLoading();
  const [adDetails, setAdDetails] = useState(
    location?.state?.adDetails ?? null,
  );
  const [isLoading, setIsLoading] = useState(
    !location?.state?.adDetails && Boolean(id),
  );

  useEffect(() => {
    if (location?.state?.adDetails) return undefined;
    if (!id) return undefined;
    let cancelled = false;
    setIsLoading(true);
    showLoader(true);
    ApiServices.httpGet(GET_AD(id))
      .then((response) => {
        if (cancelled) return;
        const record = response?.result ?? response;
        setAdDetails(mapAdToDetails(record));
      })
      .catch(() => {
        if (!cancelled) setAdDetails(null);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
          showLoader(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const details = useMemo(() => {
    const ad = adDetails || {};
    return [
      { label: "Banner Name", value: valueOrDash(ad.bannerName) },
      { label: "Position", value: valueOrDash(ad.position) },
      {
        label: "Campaign Duration",
        value: formatDateRange(ad.startDate, ad.endDate),
      },
      { label: "Status", value: valueOrDash(ad.status) },
    ];
  }, [adDetails]);

  const handleGoToAdList = () => {
    if (adDetails?.distributorId) {
      history.push(
        "/distributor/" + adDetails?.distributorId + "/ad-management",
      );
    } else {
      history.push("/distributors");
    }
  };

  if (isLoading) return null;

  if (!adDetails?.isPurchased) {
    return (
      <PurchaseSuccess
        title="Advertisement was not purchased successfully."
        subtitle="We couldn't confirm your payment. Please try again or contact support if the issue persists."
        icon={
          <ErrorOutlineIcon
            sx={{ fontSize: 64, color: "#dc2626" }}
            aria-hidden="true"
          />
        }
        primaryButton={{
          label: "Go to Advertisement List",
          onClick: handleGoToAdList,
        }}
      />
    );
  }

  return (
    <PurchaseSuccess
      title="Your advertisement has been purchased and is now active!"
      details={details}
      primaryButton={{
        label: "Go to Advertisement List",
        onClick: handleGoToAdList,
      }}
    />
  );
};

export default AdPaymentSuccess;
